import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Heart, Facebook, Youtube } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary-900 text-white" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-2">
                Stay Connected
              </h3>
              <p className="text-slate-300">
                Join our newsletter for weekly updates, events, and inspiration.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-slate-400 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20 transition-all"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-secondary text-white rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg hover:-translate-y-0.5"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Church Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-heading text-xl font-bold">G</span>
              </div>
              <div>
                <p className="font-heading text-lg font-semibold leading-tight">Geneva</p>
                <p className="text-xs text-slate-400 tracking-wide">United Methodist Church</p>
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Open Hearts, Open Minds, Open Doors... We welcome all who seek to love the Lord.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="YouTube"
                data-testid="footer-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-secondary">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Ministries', path: '/ministries' },
                { name: 'Calendar', path: '/calendar' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-secondary transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Times */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-secondary">Service Times</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-white">Sunday Worship</p>
                  <p className="text-sm text-slate-300">9:30 AM - Blended Worship</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-white">Sunday School</p>
                  <p className="text-sm text-slate-300">10:45 AM - All Ages</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-secondary">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-slate-300">89 South Broadway</p>
                  <p className="text-sm text-slate-300">Geneva, Ohio 44041</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0" />
                <a href="tel:440-466-2817" className="text-sm text-slate-300 hover:text-secondary transition-colors">
                  (440) 466-2817
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                <a href="mailto:genevaumchurch@windstream.net" className="text-sm text-slate-300 hover:text-secondary transition-colors break-all">
                  genevaumchurch@windstream.net
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © {currentYear} Geneva United Methodist Church. All rights reserved.
            </p>
            <div className="flex items-center gap-1 text-sm text-slate-400">
              Made with <Heart className="w-4 h-4 text-secondary mx-1" /> in Geneva, Ohio
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
